// Where the work is, for VERA Field: each job's place (its own address, or its boat's or client's marina or address),
// found on the map once and remembered on this device, and the job the person is at right now from where their
// device is. Nothing is tracked: the device's position is read only while Location is on (lib/location).

import type { CrmRecord, CrmType } from "./crm";
import { findPlace } from "./weather";
import type { HomePlace } from "./homePrefs";

const PLACE_FIELD = /address|location|marina|harbou?r|dock|slip|site|place|port|street|city/i;

/** The words that say where a record is, from its own fields or the records it links to (boat → client). */
export function placeText(t: CrmType, r: CrmRecord, types: CrmType[], lists: Record<string, CrmRecord[]>, depth = 0): string | null {
  const own = t.fields.filter((f) => !f.hidden && (f.kind === "text" || f.kind === "longtext") && (PLACE_FIELD.test(f.id) || PLACE_FIELD.test(f.name)))
    .map((f) => r.values[f.id]).find((v): v is string => typeof v === "string" && v.trim().length > 3);
  if (own) return own.trim().slice(0, 200);
  if (depth > 1) return null;
  for (const f of t.fields.filter((x) => x.kind === "link" && x.linkTo)) {
    const id = r.values[f.id];
    const lt = types.find((x) => x.id === f.linkTo);
    const linked = typeof id === "string" && lt ? (lists[lt.id] ?? []).find((x) => x.id === id) : undefined;
    if (lt && linked) { const p = placeText(lt, linked, types, lists, depth + 1); if (p) return p; }
  }
  return null;
}

const KEY = (q: string) => `vera-geo-${q.toLowerCase()}`;

/** A place on the map, found once per text and remembered on this device. */
export async function geocode(q: string): Promise<HomePlace | null> {
  try { const hit = localStorage.getItem(KEY(q)); if (hit) return hit === "none" ? null : (JSON.parse(hit) as HomePlace); } catch { /* no storage */ }
  try { const p = await findPlace(q); try { localStorage.setItem(KEY(q), JSON.stringify(p)); } catch { /* ignore */ } return p; }
  catch { try { localStorage.setItem(KEY(q), "none"); } catch { /* ignore */ } return null; }
}

/** Metres between two points (haversine). */
export function metres(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6_371_000, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** "here" (under 400 m), "120 m", "3.2 km", "41 km". */
export const distanceText = (m: number) => (m < 400 ? "here" : m < 1000 ? `${Math.round(m / 10) * 10} m` : m < 20_000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m / 1000)} km`);

export type JobPlace = { job: CrmRecord; place: HomePlace; metres: number | null };

/** The open jobs with a place on the map, nearest first when the device's position is known. */
export async function jobPlaces(jobs: CrmRecord[], t: CrmType, types: CrmType[], lists: Record<string, CrmRecord[]>, here?: { lat: number; lon: number } | null): Promise<JobPlace[]> {
  const out: JobPlace[] = [];
  for (const job of jobs.slice(0, 40)) {
    const q = placeText(t, job, types, lists);
    if (!q) continue;
    const place = await geocode(q);
    if (place) out.push({ job, place, metres: here ? metres(here, place) : null });
  }
  return out.sort((a, b) => (a.metres ?? Infinity) - (b.metres ?? Infinity));
}
